import { useState, useEffect, useMemo } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { FileText, Download, RefreshCw, Plus, Calendar, User } from "lucide-react";
import { toast } from "sonner";
import { authFetch, API } from "../utils/api";

/**
 * AdminInvoicingManager — admin picks a participant + date range, and the
 * system aggregates every COMPLETED shift into a single consolidated invoice
 * numbered INV-{FirstInitial+FirstTwoLastNameInitials}-###.
 */
export function AdminInvoicingManager() {
  const [participants, setParticipants] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [form, setForm] = useState({
    user_id: "",
    from_date: defaultFrom(),
    to_date: today(),
    due_in_days: 7,
  });

  const load = async () => {
    setLoading(true);
    const [uRes, iRes] = await Promise.all([
      authFetch(`${API}/admin/users?limit=500`),
      authFetch(`${API}/admin/invoices`),
    ]);
    if (uRes.ok) {
      const u = await uRes.json();
      const list = Array.isArray(u) ? u : u.users || [];
      setParticipants(list.filter((x) => x.user_type === "participant"));
    }
    if (iRes.ok) setInvoices(await iRes.json());
    setLoading(false);
  };
  useEffect(() => {
    load();
  }, []);

  const selectedParticipant = useMemo(
    () => participants.find((p) => p.user_id === form.user_id),
    [participants, form.user_id],
  );

  const predictedPrefix = useMemo(() => {
    const parts = (selectedParticipant?.name || "").trim().split(/\s+/);
    if (!parts[0]) return "XXX";
    const p1 = (parts[0][0] || "X").toUpperCase();
    const last = parts.length > 1 ? parts[parts.length - 1] : parts[0].slice(1) || parts[0];
    const p2 = (last.slice(0, 2) || "XX").toUpperCase();
    return (p1 + p2).padEnd(3, "X").slice(0, 3);
  }, [selectedParticipant]);

  const generate = async () => {
    if (!form.user_id || !form.from_date || !form.to_date) {
      toast.error("Pick a participant and a date range");
      return;
    }
    setGenerating(true);
    try {
      const r = await authFetch(`${API}/admin/invoices/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (r.ok) {
        const d = await r.json();
        toast.success(`Invoice ${d.invoice_no} created — ${d.line_items.length} shifts, total $${d.total.toFixed(2)}`);
        await load();
      } else {
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || "Couldn't generate invoice");
      }
    } finally {
      setGenerating(false);
    }
  };

  const downloadPdf = async (invoice) => {
    try {
      const r = await authFetch(`${API}/admin/invoices/${invoice.invoice_id}/pdf`);
      if (!r.ok) {
        toast.error("Couldn't download invoice PDF");
        return;
      }
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${invoice.invoice_no}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-invoicing-manager">
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <FileText className="w-4 h-4 text-[#4FA3C7]" /> Generate consolidated invoice
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Aggregates every <strong>completed</strong> shift for a participant in the selected period. Auto-numbered as{" "}
              <code className="bg-slate-100 px-1 py-0.5 rounded">INV-{predictedPrefix}-###</code>.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="md:col-span-2">
            <Label className="text-xs">Participant</Label>
            <select
              value={form.user_id}
              onChange={(e) => setForm({ ...form, user_id: e.target.value })}
              className="mt-1 w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
              data-testid="invoice-participant"
            >
              <option value="">— select —</option>
              {participants.map((p) => (
                <option key={p.user_id} value={p.user_id}>
                  {p.name} ({p.email})
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-xs">Period from</Label>
            <Input
              type="date"
              value={form.from_date}
              onChange={(e) => setForm({ ...form, from_date: e.target.value })}
              className="mt-1"
              data-testid="invoice-from"
            />
          </div>
          <div>
            <Label className="text-xs">Period to</Label>
            <Input
              type="date"
              value={form.to_date}
              onChange={(e) => setForm({ ...form, to_date: e.target.value })}
              className="mt-1"
              data-testid="invoice-to"
            />
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <div className="text-xs text-slate-500">
            {selectedParticipant ? (
              <>
                <strong className="text-slate-700">{selectedParticipant.name}</strong>
                {selectedParticipant.ndis_number && <> · NDIS {selectedParticipant.ndis_number}</>}
                {selectedParticipant.ndis_plan_type && <> · {selectedParticipant.ndis_plan_type}</>}
              </>
            ) : (
              "Pick a participant to preview their invoice prefix."
            )}
          </div>
          <Button
            onClick={generate}
            disabled={generating || !form.user_id}
            className="bg-[#87CEEB] hover:bg-[#6BB8DB] text-white"
            data-testid="invoice-generate-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            {generating ? "Generating…" : "Generate invoice"}
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-100">
        <div className="flex items-center justify-between p-4 border-b border-slate-100">
          <h3 className="font-semibold text-slate-900">Invoices</h3>
          <Button size="sm" variant="outline" onClick={load} data-testid="invoices-refresh">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                <th className="text-left p-3 font-medium">Invoice #</th>
                <th className="text-left p-3 font-medium">Participant</th>
                <th className="text-left p-3 font-medium">Period</th>
                <th className="text-left p-3 font-medium">Shifts</th>
                <th className="text-left p-3 font-medium">Total</th>
                <th className="text-left p-3 font-medium">Status</th>
                <th className="text-left p-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={7} className="p-6 text-center text-slate-400">
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && invoices.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-6 text-center text-slate-400">
                    No invoices yet. Generate one above.
                  </td>
                </tr>
              )}
              {invoices.map((inv) => (
                <tr key={inv.invoice_id} className="border-t" data-testid={`invoice-row-${inv.invoice_no}`}>
                  <td className="p-3 font-mono text-slate-900">{inv.invoice_no}</td>
                  <td className="p-3">
                    <div className="flex items-center gap-1 text-slate-700">
                      <User className="w-3.5 h-3.5 text-slate-400" />
                      {inv.participant_name}
                    </div>
                  </td>
                  <td className="p-3 text-slate-600">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5 text-slate-400" />
                      {inv.period_from} → {inv.period_to}
                    </div>
                  </td>
                  <td className="p-3 text-slate-600">{(inv.line_items || []).length}</td>
                  <td className="p-3 font-semibold text-slate-900">${(inv.total || 0).toFixed(2)}</td>
                  <td className="p-3">
                    <Badge className="bg-emerald-100 text-emerald-700 border-0">{inv.status || "issued"}</Badge>
                  </td>
                  <td className="p-3">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 text-xs"
                      onClick={() => downloadPdf(inv)}
                      data-testid={`invoice-pdf-${inv.invoice_no}`}
                    >
                      <Download className="w-3.5 h-3.5 mr-1" /> PDF
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function today() {
  return new Date().toISOString().slice(0, 10);
}
function defaultFrom() {
  const d = new Date();
  d.setDate(d.getDate() - 14);
  return d.toISOString().slice(0, 10);
}
